import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Quote, ShieldCheck, Star } from "lucide-react";

export const PatientJourneys = ({ title, subtitle }) => {
    const [area, setArea] = useState("");
    const { data: stories = [] } = useQuery({
        queryKey: ["consult-stories"],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consult/stories`)).json(),
        staleTime: 60000,
    });

    const areas = useMemo(() => {
        const map = new Map();
        stories.forEach((s) => s.area_key && map.set(s.area_key, s.area_name || s.area_key));
        return [...map.entries()];
    }, [stories]);

    const rows = area ? stories.filter((s) => s.area_key === area) : stories;
    if (stories.length === 0) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 mt-12 md:mt-16" data-testid="patient-journeys-section">
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
                {title || "Patient journeys"}
            </h2>
            {subtitle && <p className="mt-3 text-zinc-400 max-w-2xl">{subtitle}</p>}

            {areas.length > 1 && (
                <div className="mt-7 flex flex-wrap gap-2">
                    <button onClick={() => setArea("")} data-testid="journeys-filter-all"
                        className={`px-4 py-2 border text-xs transition-colors ${
                            area === "" ? "border-gold text-gold bg-gold/[0.07]" : "border-white/12 text-zinc-300 hover:border-white/30"
                        }`}>All</button>
                    {areas.map(([key, name]) => (
                        <button key={key} onClick={() => setArea(key)} data-testid={`journeys-filter-${key}`}
                            className={`px-4 py-2 border text-xs transition-colors ${
                                area === key ? "border-gold text-gold bg-gold/[0.07]" : "border-white/12 text-zinc-300 hover:border-white/30"
                            }`}>{name}</button>
                    ))}
                </div>
            )}

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rows.map((s) => (
                    <figure key={s.id} data-testid={`journey-card-${s.id}`}
                        className="p-6 border border-white/10 bg-bg1 flex flex-col">
                        <Quote className="w-5 h-5 text-gold" />
                        <div className="mt-4 flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} className={`w-3.5 h-3.5 ${n <= (s.rating || 5) ? "text-gold fill-gold" : "text-white/15"}`} />
                            ))}
                        </div>
                        {s.title && <div className="mt-4 font-display text-lg uppercase tracking-tight leading-tight">{s.title}</div>}
                        <blockquote className="mt-3 text-sm text-zinc-300 leading-relaxed flex-1 whitespace-pre-wrap">{s.body}</blockquote>
                        <figcaption className="mt-5 pt-4 border-t border-white/10">
                            <div className="text-sm text-white">
                                {s.display_name}
                                {s.age ? `, ${s.age}` : ""}{s.city ? ` · ${s.city}` : ""}
                            </div>
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 mt-1 flex items-center gap-1.5">
                                <ShieldCheck className="w-3 h-3 text-lime" />
                                {s.is_anonymous ? "SHARED ANONYMOUSLY" : "SHARED WITH CONSENT"}
                                {s.area_name ? ` · ${s.area_name.toUpperCase()}` : ""}
                            </div>
                        </figcaption>
                    </figure>
                ))}
            </div>

            <p className="mt-6 text-xs text-zinc-500 max-w-3xl leading-relaxed">
                Every story is published only with the patient's written consent, and journeys from sensitive areas of
                care are always kept anonymous. Individual experiences vary — nothing here is a promise of a specific
                outcome or a cure.
            </p>
        </section>
    );
};
